import React from "react";
import styled from "styled-components";
import Main from "./components/Main";
import Header from "./components/Header";
import GlobalModal from "./components/GlobalModal";
import ModalSettings from "./components/ModalSettings";
import { getRouteByPath } from "./routes";
import { withRouter } from "react-router-dom";
import { StyleProvider, withNavigation } from "./services/visualLibraryService";
import EventsApi from "./helpers/key-handler";
import { withAppState, getAppState } from "./state";
import getMenuItems from "./components/MainMenuContainer/menuItems";
import { config } from "./config/config";
import { i18n } from "./services/i18nService";
import { getSdk } from "./services/sdkService";
import { executeDeepLinkAction } from "./services/deepLinkService";
import { getLogger, LoggerOverlay } from "./utils/logger";
import { getThemeObject } from "./utils/getThemeObject";
import { getDefaultPreferredLanguage } from "./utils/perferedLanguage";
import "./styles/fonts.css";
import GlobalStyles from "./styles/global-styles";
import { useEffect, useState } from "react";
import "./styles/fonts.css";
import ReactGA from "react-ga4";
import { routes } from "./routes/routes";

import * as Sentry from "@sentry/react";
import { getGlobalConfig, getRemoteConfig, getRemoteProfileScreenBackGrounds } from "./services/configService";
import { isUserAccountActive } from "./services/accountStatusService";
import { timeToMilliseconds } from "./utils/timeStamp";
import { guestSignup } from "./services/pinLoginService";
import { getCookieByName, base64Decode } from "./utils/commonUtils";
import { isLoggedInUser } from "./services/authenticationService";
import { getCurrentProfile } from "./services/multipleProfilesService";
import { handleStorageEvent } from "./utils/SentryStorageLogger";
import { formatPathNames, sendPageViewAnalytics } from "./services/byteEventService";
import { getUpdatedProfile } from "./services/multipleProfilesService";
import { showPauseAds, showSponsorBugs } from "./utils/sponsorAdUtil";
import { isHighEndLG } from "./utils/checkLgNewModel";
import { isADMNRoute } from "./utils/tvodUtils";
import { trackEvent } from "./services/analyticsService";

const historyBackFocusStack = [];
let localHistory;
let localLocation;
let appStarted = false;
let currentComponent = null;
let previousPath = [];
let historyBackFocus = null;
let dismissModal;
let currentRouteFocus = null;
let previousRouteFocus = null;
let previousFocus = null;
let isPlaybackAnalyticsInitializated = false;
const GUEST_PROFILE_TYPE = "EV";

// GA4 ID
const ga4Id = process.env.NODE_ENV === "production" ? "G-FQF30LM34H" : "G-FQF30LM34H";
//const ga4Id = process.env.NODE_ENV === "production" ? "G-PSL99VZEE9" : "G-PSL99VZEE9";

// initializing ga4
if (window.location.href.includes("stg") || window.location.href.includes("localhost")) {
  ReactGA.initialize(ga4Id, {
    gaOptions: {
      debug_mode: true
    },
    gtagOptions: {
      debug_mode: true
    }
  });
} else {
  ReactGA.initialize(ga4Id);
}

let Logger = getLogger(undefined, { enabled: false });

/**
 * Inits the debugger overlay component.
 * This should depend on the current build config.
 * @param loggerOverLay
 */
const initOverlayLogger = loggerOverLay => {
  Logger = getLogger(undefined, { enabled: true, appender: loggerOverLay });
};

/**
 * Returns whether the passed router path must have the main menu.
 * @param url The current URL location.
 * @return {boolean} The location must have the menu.
 */
const hasHeader = url => {
  if (url === "/admn") {
    return true;
  }
  if (/^\/admn\/.+/.test(url)) {
    return true;
  }

  return (
    url.search(
      /\/(series|movies|.+\/series|.+\/movies)\/\d|\/(player|login|pin|landing|welcome|promotion|buyasset|buybrands|nonchangeable|signup|redeem|thankyou|search|explore|mylibrary|splash|settings|parentalControl|edit-profile|add-profile|profile-name|pin-lock|age-restriction|profile-selector|account-password|movies|series|brands|kids|arabic|deleted-profile)/
    ) !== 0
  );
};

const noHeader = url => url.search(/\/brands\/.*?(?=$|\/$)/) === 0;

/**
 * Navigates back in the router history.
 * @param history The router history.
 */
const goBack = (history, location) => {
  if (location.state?.from) {
    const { name = "/home" } = location.state?.from;
    const finalPath = name === "/" ? "/home" : name;
    history.replace(finalPath, {
      ...location.state
    });
    return;
  }
  history.goBack();
};

/**
 * Finds the current page component default focus depending on layout direction.
 * @param component
 * @return {*}
 */
const getDefaultFocus = (component = currentComponent) =>
  i18n.getDirection() === i18n.directions.RIGHT_TO_LEFT
    ? component && (component.defaultFocusPathRTL || component.defaultFocusPath)
    : component && (component.defaultFocusPathLTR || component.defaultFocusPath);

/**
 * Sets the focus to the previous or default focus element in that order.
 * @return {boolean} Whether the focus could be set.
 */
const focusPreviousDefault = () => {
  if (currentComponent && currentComponent.props.setFocus) {
    const defaultFocus = previousFocus || getDefaultFocus();
    const isLivePage = currentComponent.props?.name?.toLowerCase() === "live" ? true : false;
    currentComponent.props.setFocus(defaultFocus);
    if (currentComponent.player && currentComponent.showQRCodeModal) {
      currentComponent.showQRCodeModal = false;
      currentComponent.isRemoveAdPopupOpen && (currentComponent.isRemoveAdPopupOpen = false);
      currentComponent.player.play();
      currentComponent.isAdStalled = false;
    } else if (isLivePage && currentComponent.state.showQRCodeModal) {
      currentComponent.toggleQRCodeModal(false);
    }
    return !!defaultFocus;
  }
  return false;
};

/**
 * Bind back button.
 * First, the onBack method of the current page will be called if present.
 * If the method returns false or is not present, goBack in location will be called.
 */
/* 
Here new checks for forcelogout added beacuse not allow  Back action for force logout popup 
*/
EventsApi({
  BACK: () =>
    !getAppState().isProfileDeletedModal &&
    ((!getAppState().forceLogout && dismissModal && dismissModal(focusPreviousDefault)) ||
      (currentComponent && currentComponent.onBack && !getAppState().forceLogout && currentComponent.onBack()) ||
      (!getAppState().forceLogout && goBack(localHistory, localLocation)))
});

/**
 * Keeps track of the current page component for future actions.
 * @param component
 * @return {*}
 */
const onRef = component => (currentComponent = component);

const RTL = i18n.directions.RIGHT_TO_LEFT;

const GlobalThemeContainer = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  ${props => props.hasHeader && "display: flex"};
`;

const StyledIframe = styled.iframe`
  border: none !important;
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: 9999; /* 1 */
`;

const ScreenSaver = () => {
  let iframeSrc = config.get().screenSaver.SCREENSAVER_ARTWORKS_PATH_KEY;

  return <StyledIframe src={iframeSrc} title="ScreenSaver" />;
};

let historyObject = null;
const handleDeepLinking = deepLinkData => executeDeepLinkAction.call({ history: historyObject }, deepLinkData);

/**
 * Global Focus HOC.
 * In case a focus must be ignored for back navigation (i.e.: modal popups)
 * the focusPath props must contain the 'passThru' string
 */
const NavigationWrapper = withNavigation(
  ({
    currentFocusPath,
    hasHeader,
    noHeader,
    initialFocus,
    setFocus,
    pauseNavigation,
    resumeNavigation,
    applicationState: { userLocale, globalModal, settingsModal, theme },
    stateActions: { setCurrentFocus }
  }) => {
    const direction = config.get().locales[userLocale] && config.get().locales[userLocale].direction;

    const idleTime = config.get().screenSaver.SCREENSAVER_IDLE_TIME_KEY;

    var visibilityChange, hidden;
    if (typeof document.hidden !== "undefined") {
      // To support the standard web browser engine
      hidden = "hidden";
      visibilityChange = "visibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      // To support the webkit engine
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }

    if (currentRouteFocus !== currentFocusPath) {
      setCurrentFocus(currentFocusPath);
    }

    if (globalModal && globalModal.dismiss) {
      dismissModal = globalModal.dismiss;
    } else if (settingsModal && settingsModal.dismiss) {
      dismissModal = settingsModal.dismiss;
    } else {
      dismissModal = null;
    }

    currentRouteFocus && currentRouteFocus.indexOf("passThru") === -1 && (previousFocus = currentRouteFocus);
    // fixme: in future rather than saving the info in constant make it way react-router suggests
    currentRouteFocus = currentFocusPath;
    const [currentPath, setCurrentPath] = useState(null);

    useEffect(() => {
      if (window.location.pathname !== "/" && window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
        const item = window.location.pathname;
        const itemName = item.path ? item.path.split("/")[1] : "";
        setTimeout(() => trackEvent("page_view", itemName, "page_view"), 1000);
      }
    }, [currentPath, window.location.pathname]);
    // State to track user interaction
    const [userInteraction, setUserInteraction] = useState(false);
    // State to track whether the screen saver is active
    const [screenSaverActive, setScreenSaverActive] = useState(false);
    let screenSaverTimer = null;

    // Function to handle user interactions (e.g., clicks)
    const handleUserInteraction = () => {
      setUserInteraction(true);
      setScreenSaverActive(false);
      startScreenSaverTimer();
    };

    const startScreenSaverTimer = () => {
      if (screenSaverTimer !== null) clearTimeout(screenSaverTimer);
      if (getAppState().isOnline === true) {
        screenSaverTimer = setTimeout(() => {
          setScreenSaverActive(true);
        }, idleTime);
      }
    };

    useEffect(() => {
      startScreenSaverTimer();
      document.addEventListener("keydown", handleUserInteraction);
      document.addEventListener("click", handleUserInteraction);
      document.addEventListener(visibilityChange, handleUserInteraction);

      return () => {
        if (screenSaverTimer !== null) clearTimeout(screenSaverTimer);
        document.removeEventListener("keydown", handleUserInteraction);
        document.removeEventListener("click", handleUserInteraction);
        document.removeEventListener(visibilityChange, handleUserInteraction);
      };
    });

    useEffect(() => {
      if (userInteraction) {
        startScreenSaverTimer();
      }
    }, [userInteraction]);

    useEffect(() => {
      // reset the screen saver timer if the path change (polling or user interaction)
      if (localHistory && localHistory.location && localHistory.location.referrer !== localHistory.location.pathname) {
        handleUserInteraction();
      }
    }, [localHistory && localHistory.location && localHistory.location.pathname]);

    useEffect(() => {
      //--------- Adding localstorage Event listerner ---
      window.addEventListener("storage", handleStorageEvent, false);
      return () => {
        window.removeEventListener("storage");
      };
    }, []);

    const isRootPath = window.location.pathname !== "/" ? true : false;
    const isPlayerPath = window.location.pathname === routes.player ? true : false;
    const isLivePath = window.location.pathname === routes.live ? true : false;
    return (
      <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
        <StyleProvider theme={getThemeObject(direction, theme)}>
          {(!globalModal || !settingsModal) && screenSaverActive && !isPlayerPath && !isLivePath ? (
            <ScreenSaver />
          ) : null}
          <GlobalStyles />
          {globalModal && <GlobalModal modalConfig={globalModal} />}
          {settingsModal && <ModalSettings modalConfig={settingsModal} />}
          <GlobalThemeContainer hasHeader={hasHeader} noHeader={noHeader}>
            {hasHeader && isRootPath && <Header />}
            <Main
              initialFocus={initialFocus}
              onRef={onRef}
              from={currentPath}
              setFocus={setFocus}
              logger={Logger}
              pauseNavigation={pauseNavigation}
              resumeNavigation={resumeNavigation}
            />
          </GlobalThemeContainer>
        </StyleProvider>
      </Sentry.ErrorBoundary>
    );
  }
);
/**
 * Checks whether the current navigation fits the conditions to have an initialFocus.
 * It checks if is Home and is not the first load or is any other root level page.
 * @param page The page router to load.
 * @param applicationState The application state object.
 * @return {boolean} The assertion is right.
 */
const isFilteredFromInitialFocus = (page, applicationState) =>
  appStarted && page.isRoot && !applicationState.userLogged;

/**
 * Returns the initial default focused element for the current route.
 * @param location The current router location.
 * @param applicationState The application state object.
 * @return {boolean|*|string} The default focus if available.
 */
const getInitialFocus = (location, applicationState) => {
  if (applicationState.globalModal || applicationState.settingsModal) {
    return undefined;
  }
  const toFocus = getRouteByPath(location.pathname);
  return toFocus && !isFilteredFromInitialFocus(toFocus, applicationState) && getDefaultFocus(toFocus.component);
};

/**
 * Sets referrer to history.location
 * @param {Object} history
 */
const setReferrer = history => {
  if (!previousPath.includes(history.location.pathname)) {
    previousPath.push(history.location.pathname);
    history.location.referrer = previousPath[0];
    previousPath.length > 1 && previousPath.shift();
  }
};

const getDefaultMenuFocusedItem = location => {
  let activeMainMenuItemIndex = 0;
  if (appStarted) {
    activeMainMenuItemIndex = getMenuItems().findIndex(item => {
      return location.pathname === item.path;
    });
  }
  return Math.max(activeMainMenuItemIndex, 0);
};

/**
 * Adds the last focus to the current page focus history depending on string flags ("passThru").
 */
const addToFocusHistory = () => {
  const currentRoute = (currentRouteFocus && currentRouteFocus.indexOf("passThru")) || -1;
  if (currentRoute !== -1) {
    historyBackFocusStack.push(previousFocus);
  } else {
    historyBackFocusStack.push(currentRouteFocus);
  }
};

function App(props) {
  return (
    <>
      <AppContent />
    </>
  );
}

function setConditionalBackground(history) {
  const pathname = history?.location?.pathname || window.location.pathname;
  const isAdmnRoute = isADMNRoute(pathname);
  if (isAdmnRoute) {
    document.body.style.setProperty("background-image", "none", "important");
    document.body.style.setProperty("background-color", "#000", "important");
  } else {
    document.body.style.setProperty(
      "background-image",
      'url("http://mena-img-cdn-lb.aws.playco.com/prd-peg-data/default/images/brand-background-main.png")',
      "important"
    );
    document.body.style.setProperty("background-color", "", "important");
  }
}

const AppContent = withRouter(
  withAppState(({ location, history, applicationState, stateActions }) => {
    const debug = config.get().debug;
    const isHeaderShown = hasHeader(location.pathname);
    const noHeaderShown = noHeader(location.pathname) || location.pathname === "/";
    const initialFocus = getInitialFocus(location, applicationState);
    const [globalConfig, setGlobalConfig] = useState(null);
    const [remoteConfig, setRemoteConfig] = useState(null);
    const [remoteProfileScreenBackGround, setRemoteProfileScreenBackGrounds] = useState(null);
    historyObject = history;
    if (location.pathname !== previousPath[0]) {
      if (history.action === "POP") {
        historyBackFocus = historyBackFocusStack.pop() || previousRouteFocus;
        const selectedMainMenuItem = getMenuItems()[applicationState.focusedMainMenuItemIndex];
        if (selectedMainMenuItem && !selectedMainMenuItem.sticky) {
          applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
        }
      } else if (history.action === "REPLACE") {
        historyBackFocus = null;
        if (previousPath[0] !== "/") {
          // Only if app is not starting from the base route, i.e.: first launch.
          applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
        }
        if (location.state && location.state.preserveFocus) {
          historyBackFocus = currentRouteFocus;
        }
      } else if (history.action === "PUSH") {
        addToFocusHistory();
        historyBackFocus = null;
        applicationState.focusedMainMenuItemIndex = getDefaultMenuFocusedItem(location);
      }
      previousRouteFocus = currentRouteFocus;
      currentComponent = null;
    }
    // store current and previous screen for Byte+ event
    const ignoreBothPathsList = [
      "/exitAppPage",
      "/loginOrSignup",
      "/login",
      "/pin-lock",
      "/edit-profile",
      "/pin",
      "/pin-signup"
    ];
    const ignoreCurrentPathsList = ["/profile-selector"];
    // initial check to ignore the paths mentioned in ignoreBothPathsList and ignoreCurrentPathsList
    if (
      formatPathNames(location.pathname) !== "player_page" &&
      ignoreCurrentPathsList.indexOf(location.pathname) === -1 &&
      ignoreBothPathsList.indexOf(location.pathname) === -1 &&
      ignoreBothPathsList.indexOf(previousPath[0]) === -1 &&
      location.pathname !== "/categoriesMenuPage"
    ) {
      //  case 1: if previous path is categories, prev and curr path are not the same and prevpath is not any path mentioned in ignoreCurrentPathsList
      if (
        previousPath[0] === "/categoriesMenuPage" &&
        formatPathNames(location.pathname) !== sessionStorage.getItem("currentScreen") &&
        ignoreCurrentPathsList.indexOf(previousPath[0]) === -1
      ) {
        sessionStorage.setItem("prevScreen", sessionStorage.getItem("currentScreen"));
      }
      // special case: for prevpath is profile selector
      if (previousPath[0] === "/profile-selector") {
        // we check for prevpath null for handling the first launch case where cur and prev will not be equal but the path has to change. The rest case we will check cur and prev not equal
        // if(sessionStorage.getItem("prevScreen")=="null" || formatPathNames(location.pathname)!==sessionStorage.getItem("currentScreen")){
        sessionStorage.setItem("prevScreen", sessionStorage.getItem("currentScreen"));
        // }
      }
      // case 2: current path is not categories menu page then set current screen
      location.pathname !== "/categoriesMenuPage" &&
        sessionStorage.setItem("currentScreen", formatPathNames(location.pathname));
      // case 3: prevpath and currpath is not same and prepath is not categories menu page or not in ignoreCurrentPathsList
      if (
        previousPath[0] !== location.pathname &&
        previousPath[0] !== "/categoriesMenuPage" &&
        ignoreCurrentPathsList.indexOf(previousPath[0]) === -1
      ) {
        sessionStorage.setItem("prevScreen", formatPathNames(previousPath[0]));
      }
    } else if (previousPath[0] === "/pin-lock") {
      sessionStorage.setItem("prevScreen", sessionStorage.getItem("currentScreen"));
    }
    // clear series and series id if page is not player
    if (location.pathname !== "/player") {
      sessionStorage.removeItem("currentlyPlayingid");
      sessionStorage.removeItem("currentlyPlayingisSeries");
    }
    setReferrer(history);
    localHistory = history;
    localLocation = location;
    applicationState.appStarted = !appStarted ? false : getAppState().appStarted;
    appStarted = true;

    async function initPlaybackAnalytics(player, playerConfig) {
      isPlaybackAnalyticsInitializated = true;
      const sdk = await getSdk();
      applicationState.player = null;
      applicationState.playerConfig = null;
      sdk.playerManager.initPlaybackAnalytics(player, playerConfig);
    }
    !!config.get().featureToggles.playbackAnalytics &&
      applicationState.player &&
      applicationState.playerConfig &&
      initPlaybackAnalytics(applicationState.player, applicationState.playerConfig);
    useEffect(() => {
      async function fetchGlobalConfig() {
        const globalConfig = await getGlobalConfig();
        stateActions.setGlobalConfig(globalConfig);
        setGlobalConfig(globalConfig);
      }
      fetchGlobalConfig();
      return () => {
        getAppState().signupPromptActive = false;
      };
    }, []);

    useEffect(() => {
      if (globalConfig) {
        getAppState().signupPromptActive = false;
        const promptConfig = globalConfig.prompt;

        if (getAppState().userLogged && promptConfig?.premium?.active) {
          const userAccountStatus = getAppState().userAccountStatus;
          const userAddonStatus = getAppState()?.userAddonStatus;
          if (!isUserAccountActive(userAccountStatus) && !userAddonStatus) {
            getAppState().signupPromptTimer = timeToMilliseconds(promptConfig?.premium?.time) || 5000;
            getAppState().signupPromptActive = true;
          }
        }
        if (!getAppState().userLogged && promptConfig?.signup?.active) {
          getAppState().signupPromptTimer = timeToMilliseconds(promptConfig?.signup?.time) || 5000;
          getAppState().signupPromptActive = true;
        }
        stateActions.setPlayerTimerConfig(globalConfig.playertimers);
        stateActions.setWidevinePlayerConfig(globalConfig?.widevine || {});
      }
    }, [globalConfig, applicationState.userLogged, getAppState().userAccountStatus, getAppState().userAddonStatus]);

    useEffect(() => {
      document.addEventListener("isSelectedProfileDeletedEvent", e => {
        getAppState().isProfileDeletedModal = true;
        history.replace("/deleted-profile");
        return;
      });

      async function setLanguage() {
        const currentProfile = await getCurrentProfile();
        const currentLanguage = localStorage.getItem("locale");
        const lang = currentProfile?.language || currentLanguage || getAppState().userLocale;
        getAppState().userLocale = lang;
        stateActions.setLocale(lang);
        const sdk = await getSdk();
        await sdk.userManager.setLanguage(lang);
        return lang;
      }
      async function fetchGuestSignupData() {
        let guestProfile = {};
        try {
          if (getCookieByName("guest-profile")) {
            guestProfile = (await base64Decode(getCookieByName("guest-profile"))) || {}; // base64Decode
            const currentProfile = await getCurrentProfile();
            const guestUser = JSON.parse(guestProfile);
            localStorage.setItem("globalUserId", guestUser?.globalUserId);
            if (!currentProfile) {
              guestProfile = await getUpdatedProfile(guestUser?.globalUserId, getAppState().country);
            }
          } else {
            // Initiate Guestsignup call , this is base call to get Guest userGlobalId and profileType. Based of these values entire Avod based Guest flow working including playbacks.
            const lang = await setLanguage();
            guestProfile = await guestSignup(lang, getAppState().country);
            localStorage.setItem("globalUserId", guestProfile?.user?.globalUserId);
          }

          if (guestProfile) {
            //if (guestProfile?.profileType === GUEST_PROFILE_TYPE)
            const currentProfile = await getCurrentProfile();
            getAppState().isGuestProfile = true;
            getAppState().currentProfileType = guestProfile?.profileType || GUEST_PROFILE_TYPE;
            stateActions.setCurrentProfileId(currentProfile?.profileId);
            setLanguage();
          }
        } catch (error) {
          // if getUpdatedProfile API fail, we have to call guest signup
          const currentLanguage = localStorage.getItem("locale");
          const lang = currentLanguage || getAppState().userLocale;
          guestProfile = await guestSignup(lang, getAppState().country);
        }
        // Set preferred language in application state
        applicationState.preferredLanguage = getDefaultPreferredLanguage(applicationState.preferredLanguage);
      }

      if (!getAppState().userLogged) {
        fetchGuestSignupData();
      } else {
        setLanguage();
      }
    }, [getAppState().userLogged]);

    useEffect(() => {
      if (globalConfig) {
        const onboardingConfig = globalConfig.onboarding;
        if (getAppState().userLogged && onboardingConfig?.loggedin) {
          stateActions.setDefaultLandingPath(onboardingConfig?.loggedin?.defaultLanding);
        } else {
          // getAppState().defaultLandingPath = onboardingConfig?.guest?.defaultLanding;
          stateActions.setDefaultLandingPath(onboardingConfig?.guest?.defaultLanding);
        }
      }
    }, [globalConfig, getAppState().userLogged, getAppState().defaultLandingPath]);

    useEffect(() => {
      async function fetchRemoteConfig() {
        const remoteConfig = await getRemoteConfig();
        setRemoteConfig(remoteConfig);
        stateActions.setRemoteConfigContext(remoteConfig);
      }
      async function fetchRemoteProfileScreenBackGrounds() {
        const remoteProfileScreenBackGrounds = await getRemoteProfileScreenBackGrounds();
        setRemoteProfileScreenBackGrounds(remoteProfileScreenBackGrounds);
      }
      fetchRemoteConfig();
      fetchRemoteProfileScreenBackGrounds();
    }, []);

    useEffect(() => {
      async function findActiveUserLandingPath() {
        if (remoteConfig) {
          const activeUserRedirectionConfig =
            remoteConfig?.lgtv?.active_user_redirection || remoteConfig?.active_user_redirection || null;

          getAppState().activeUserRedirectionConfig = activeUserRedirectionConfig;
        }
        if (remoteProfileScreenBackGround) {
          getAppState().remoteProfileScreenBackGround = remoteProfileScreenBackGround;
        }
      }
      findActiveUserLandingPath();
    }, [remoteConfig, remoteProfileScreenBackGround]);

    // Ad tech project - Checking Subscription Names Config - Start
    useEffect(() => {
      const isLoggedIn = getAppState().userLogged;
      const addons = getAppState().addons;
      if (!isHighEndLG()) {
        getAppState().showSponsorBugs = false;
        getAppState().showPauseAds = false;
      } else if (remoteConfig && isLoggedIn && addons) {
        const isShowSponsorBug = showSponsorBugs(addons, remoteConfig);
        const isShowPauseAds = showPauseAds(addons, remoteConfig);
        // Setting Global State based on user subscription
        if (isShowSponsorBug) {
          getAppState().showSponsorBugs = false;
        } else {
          getAppState().showSponsorBugs = true;
        }
        // Setting Global State based on user subscription
        if (isShowPauseAds) {
          getAppState().showPauseAds = false;
        } else {
          getAppState().showPauseAds = true;
        }
      } else {
        getAppState().showSponsorBugs = true;
        getAppState().showPauseAds = true;
      }
    }, [
      remoteConfig,
      getAppState().userLogged,
      getAppState().addons,
      getAppState().showSponsorBugs,
      getAppState().showPauseAds
    ]);
    // Ad tech project - Checking Subscription Names Config - End

    // Call the background function on route change
    // useEffect(() => {
    //   setConditionalBackground(history);
    //   return () => {
    //     document.body.style.removeProperty("background-image");
    //     document.body.style.removeProperty("background-color");
    //   };
    // }, [history.location.pathname]);

    return (
      <>
        {(debug.enabled || window.__debugEnabled) && <LoggerOverlay onRef={ref => initOverlayLogger(ref)} />}
        <NavigationWrapper
          hasHeader={isHeaderShown}
          noHeader={noHeaderShown}
          applicationState={applicationState}
          stateActions={stateActions}
          initialFocus={historyBackFocus || initialFocus || ""}
        />
      </>
    );
  })
);

/**
 * Function that deals with setting up the listeners that need to be created during application bootstrapping.
 */
async function setupListeners() {
  const sdk = await getSdk();
  sdk.systemManager.application.getDeepLinkTitle && sdk.systemManager.application.on("deepLink", handleDeepLinking);
  sdk.systemManager.network.on("networkDisconnected", () => {
    Logger.warn("DISCONNECTED", "Network");
    getAppState().isOnline = false;
  });

  sdk.systemManager.network.on("networkConnected", () => {
    Logger.warn("CONNECTED", "Network");
    getAppState().isOnline = true;
  });
}

setupListeners();

export default App;
